import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchEpicCutover } from '../../lib/api/epics';
import type { CutoverRouteView } from '../../lib/api/epics';
import { StatusBadge } from '../ui/StatusBadge';
import type { StatusTone } from '../ui/StatusBadge';

/**
 * FORGE-276 M1 — runtime-strangler cutover board. Renders the per-route flip
 * state (monolith → shadow → canary → cut-over → retired) for a migration epic
 * running in `strangler: runtime` mode, grouped by owning project, with an
 * off-monolith / cut-over progress rollup and each route's shadow-parity tally.
 *
 * Null-discipline (like StuckDiagnosisCard / PRTrackingCard): an epic with no
 * CUTOVER# rows — every non-runtime-strangler epic — renders NOTHING, so the
 * card never implies a feature the epic isn't using.
 */

const STATE_TONE: Record<CutoverRouteView['state'], StatusTone> = {
  monolith: 'neutral',
  shadow: 'info',
  canary: 'warning',
  'cut-over': 'success',
  retired: 'purple',
};
const STATE_ICON: Record<CutoverRouteView['state'], string> = {
  monolith: '🏛️',
  shadow: '👥',
  canary: '🐤',
  'cut-over': '✅',
  retired: '🗑️',
};
const STATE_ORDER: CutoverRouteView['state'][] = ['monolith', 'shadow', 'canary', 'cut-over', 'retired'];

export const CutoverBoard: React.FC<{ epicId: string }> = ({ epicId }) => {
  const query = useQuery({
    queryKey: ['epic-cutover', epicId],
    queryFn: () => fetchEpicCutover(epicId),
    // Cheap poll — routes flip via operator /cutover commands out of band.
    refetchInterval: 30_000,
    retry: false,
  });

  const board = query.data;
  // Nothing to show → render nothing (not a runtime-strangler epic, or no facade yet).
  if (!board || board.summary.total === 0) return null;

  const { routes, summary } = board;

  // Group by owner, owners sorted, routes sorted within.
  const byOwner = new Map<string, CutoverRouteView[]>();
  for (const r of routes) {
    const o = r.owner || '(unassigned)';
    if (!byOwner.has(o)) byOwner.set(o, []);
    byOwner.get(o)!.push(r);
  }
  const owners = [...byOwner.keys()].sort((a, b) => a.localeCompare(b));

  const pct = (n: number) => `${Math.round(n * 100)}%`;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">🧭 Runtime cutover</h3>
        <span className="text-xs text-gray-500">
          {summary.total} route{summary.total === 1 ? '' : 's'} · {pct(summary.progress)} off-monolith · {pct(summary.migrated)} cut-over
        </span>
      </div>

      {/* State rollup chips */}
      <div className="mb-4 flex flex-wrap gap-2">
        {STATE_ORDER.filter((s) => (summary.by_state[s] ?? 0) > 0).map((s) => (
          <StatusBadge key={s} tone={STATE_TONE[s]}>
            {STATE_ICON[s]} {summary.by_state[s]} {s}
          </StatusBadge>
        ))}
      </div>

      {/* Per-owner route tables */}
      <div className="space-y-4">
        {owners.map((owner) => (
          <div key={owner}>
            <div className="mb-1 font-mono text-xs text-gray-500">{owner}</div>
            <div className="divide-y divide-gray-100">
              {byOwner.get(owner)!.map((r) => {
                const runs = r.parity?.shadow_runs ?? 0;
                const parityLabel = runs > 0
                  ? `${r.parity.shadow_matched}/${runs} shadow${r.parity.last_result ? ` · ${r.parity.last_result}` : ''}`
                  : '';
                return (
                  <div key={r.route} className="flex items-center justify-between gap-2 py-1.5">
                    <code className="truncate font-mono text-xs text-gray-700">{r.route}</code>
                    <div className="flex shrink-0 items-center gap-2">
                      {parityLabel && (
                        <span className="text-[11px] text-gray-400">{parityLabel}</span>
                      )}
                      <StatusBadge tone={STATE_TONE[r.state]}>
                        {STATE_ICON[r.state]} {r.state}
                      </StatusBadge>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <p className="mt-3 text-[11px] text-gray-400">
        Flip a route with <code className="font-mono">/cutover &lt;METHOD&gt; &lt;path&gt; &lt;state&gt;</code> on the epic issue.
        Promotions to canary/cut-over require the shadow-parity gate to hold.
      </p>
    </div>
  );
};
